/**
 * Token Manager - token lifecycle management
 * Handles token storage, retrieval, expiration checking, and state management
 */
import moment from "moment";
import { TOKEN_STORAGE } from "../api/constants";

/**
 * Token Manager - Provides single source of truth for token state
 * All token operations go through this manager
 */
export const tokenManager = {
  /**
   * Get current token from storage
   * @returns {Object|null} Token object with {value, expires} or null if not found
   */
  getToken() {
    try {
      const stored = localStorage.getItem(TOKEN_STORAGE.KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error("Error reading token from storage:", error);
      return null;
    }
  },

  /**
   * Set token in storage
   * @param {string} token - JWT token value
   * @param {number} [expiresInDays=30] - Days until token expires
   * @returns {boolean} Success status
   */
  setToken(token, expiresInDays = 30) {
    try {
      const expires = moment().add(expiresInDays, "days").valueOf();
      const tokenData = { value: token, expires };
      localStorage.setItem(TOKEN_STORAGE.KEY, JSON.stringify(tokenData));

      return true;
    } catch (error) {
      console.error("Error saving token to storage:", error);
      return false;
    }
  },

  /**
   * Remove token from storage
   * @returns {boolean} Success status
   */
  clearToken() {
    try {
      localStorage.removeItem(TOKEN_STORAGE.KEY);
      return true;
    } catch (error) {
      console.error("Error clearing token from storage:", error);
      return false;
    }
  },

  /**
   * Check if token exists and is not expired
   * @returns {boolean} True if valid token exists
   */
  hasValidToken() {
    const token = this.getToken();
    if (!token) return false;

    const now = moment().valueOf();
    // Apply expiration buffer (check slightly before actual expiration)
    const effectiveExpiration = token.expires - TOKEN_STORAGE.EXPIRATION_BUFFER;

    return now < effectiveExpiration;
  },

  /**
   * Check if token is expired
   * @returns {boolean} True if token is expired
   */
  isExpired() {
    return !this.hasValidToken();
  },

  /**
   * Get token value if valid, null otherwise
   * @returns {string|null} JWT token string or null
   */
  getValidToken() {
    if (this.hasValidToken()) {
      return this.getToken()?.value || null;
    }
    return null;
  },

  /**
   * Get remaining time until expiration in milliseconds
   * @returns {number|null} Milliseconds until expiration, or null if no token
   */
  getTimeUntilExpiration() {
    const token = this.getToken();
    if (!token) return null;

    const now = moment().valueOf();
    const remaining = token.expires - now;

    return remaining > 0 ? remaining : 0;
  },

  /**
   * Get remaining time until expiration as human-readable string
   * @returns {string|null} Human-readable time string or null if no token
   */
  getTimeUntilExpirationFormatted() {
    const ms = this.getTimeUntilExpiration();
    if (ms === null) return null;

    if (ms <= 0) return "Expired";

    const duration = moment.duration(ms);
    if (duration.asDays() >= 1) {
      return `${Math.floor(duration.asDays())} day(s)`;
    }
    if (duration.asHours() >= 1) {
      return `${Math.floor(duration.asHours())} hour(s)`;
    }
    return `${Math.floor(duration.asMinutes())} minute(s)`;
  },
};

export default tokenManager;
